import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

const LIMIT_OPTIONS = [5, 10, 15, 20];

interface InvestorAnalysisSummary {
  total_holdings: number;
  total_market_value: number;
  total_market_value_billions: number;
  total_shares: number;
  max_weight: number;
  avg_weight: number;
}

interface InvestorHoldingItem {
  date?: string;
  investor_name?: string;
  symbol?: string;
  security_name?: string;
  industry_title?: string;
  weight?: number;
  weight_percentage?: number;
  market_value?: number;
  market_value_millions?: number;
  market_value_display?: string;
  shares_number?: number;
  ownership?: number;
  ownership_percentage?: number;
}

interface InvestorAnalysisApiData {
  investor_name: string;
  year: number;
  quarter: number;
  symbol_filter: string | null;
  limit: number;
  data_date: string | null;
  summary: InvestorAnalysisSummary;
  holdings_data: InvestorHoldingItem[];
  matched_investors: string[];
}

export class TraderverseInvestorAnalysis {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private apiData: InvestorAnalysisApiData | null = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
  private lastFetchedAt: Date | null = null;
  private filters = {
    investorName: 'Vanguard',
    year: 2026,
    quarter: 1,
    symbol: 'NVDA' as string | undefined,
    limit: 5
  };

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    this.filters.investorName = String(
      (config as any).investorName ?? (this.config as any).investorName ?? this.filters.investorName
    );
    this.filters.year = Number((config as any).year ?? (this.config as any).year ?? this.filters.year);
    this.filters.quarter = Number((config as any).quarter ?? (this.config as any).quarter ?? this.filters.quarter);
    this.filters.limit = Number((config as any).limit ?? (this.config as any).limit ?? this.filters.limit);
    this.filters.symbol = (config.symbol ?? this.config.symbol ?? this.filters.symbol) || undefined;

    (this.config as any).investorName = this.filters.investorName;
    (this.config as any).year = this.filters.year;
    (this.config as any).quarter = this.filters.quarter;
    (this.config as any).limit = this.filters.limit;
    this.config.symbol = this.filters.symbol;

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    return {
      headerPinned: true,
      symbol: 'NVDA',
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      isSymbolSelectable: true,
      investorName: 'Vanguard',
      year: 2026,
      quarter: 1,
      limit: 5
    } as any;
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private getYearOptions(): number[] {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, i) => currentYear - i);
  }

  private renderYearOptions(): string {
    return this.getYearOptions()
      .map(year => `<option value="${year}"${year === this.filters.year ? ' selected' : ''}>${year}</option>`)
      .join('');
  }

  private renderLimitOptions(): string {
    const options = LIMIT_OPTIONS.includes(this.filters.limit)
      ? LIMIT_OPTIONS
      : [...LIMIT_OPTIONS, this.filters.limit].sort((a, b) => a - b);
    return options
      .map(n => `<option value="${n}"${n === this.filters.limit ? ' selected' : ''}>${n}</option>`)
      .join('');
  }

  private filtersIconSvg(): string {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
  }

  private async init(): Promise<void> {
    await this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private async createWidget(): Promise<void> {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const widgetHtml = `
          <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Investor Analysis</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper>
              <div class="item">
                <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
              ${this.config.hideSymbol ? '' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                  ${this.config.symbol
                    ? getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol, type: 'stock', name: '' })
                    : '<span>Select Symbol</span>'}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
              </div>`}
              <span></span>
              <div class="filtersBox sideBar hidden">
                <div class="item">
                  <label>Investor</label>
                  <input type="text" class="investor-input" aria-label="Investor Name" data-testing-id="investor-input" value="${this.escapeAttr(this.filters.investorName)}" placeholder="e.g. Vanguard" />
                </div>
                <div class="item">
                  <label>Year</label>
                  <select class="year-select" aria-label="Year" data-testing-id="year-select">${this.renderYearOptions()}</select>
                </div>
                <div class="item">
                  <label>Quarter</label>
                  <select class="quarter-select" aria-label="Quarter" data-testing-id="quarter-select">
                    <option value="1"${this.filters.quarter === 1 ? ' selected' : ''}>Q1</option>
                    <option value="2"${this.filters.quarter === 2 ? ' selected' : ''}>Q2</option>
                    <option value="3"${this.filters.quarter === 3 ? ' selected' : ''}>Q3</option>
                    <option value="4"${this.filters.quarter === 4 ? ' selected' : ''}>Q4</option>
                  </select>
                </div>
                <div class="item">
                  <label>Limit</label>
                  <select class="limit-select" aria-label="Limit" data-testing-id="limit-select">${this.renderLimitOptions()}</select>
                </div>
              </div>
            </div>
            <div class="chartDv" id="investorAnalysisContainer" data-testing-id="investor-analysis-container"></div>
          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    await this.attachStyles();
    setupHeaderPin(this.shadowRoot, this.config);
    this.setupEventListeners();
    this.wireFilterControls();
  }

  private escapeAttr(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private setupEventListeners(): void {
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  private onSelectChange(stock: any): void {
    this.changeSymbol(stock.symbol, stock);
  }

  private wireFilterControls(): void {
    const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
    const investorInput = this.shadowRoot.querySelector('.investor-input') as HTMLInputElement | null;
    const yearSelect = this.shadowRoot.querySelector('.year-select') as HTMLSelectElement | null;
    const quarterSelect = this.shadowRoot.querySelector('.quarter-select') as HTMLSelectElement | null;
    const limitSelect = this.shadowRoot.querySelector('.limit-select') as HTMLSelectElement | null;

    if (filterBtn && filtersBox) {
      filterBtn.addEventListener('click', (ev: MouseEvent) => {
        ev.stopPropagation();
        filtersBox.classList.toggle('hidden');
      });
      filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());

      this.outsideClickHandler = (ev: MouseEvent) => {
        const anyEv: any = ev;
        const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
        const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
        if (!clickedInside && !filtersBox.classList.contains('hidden')) {
          filtersBox.classList.add('hidden');
        }
      };
      document.addEventListener('click', this.outsideClickHandler);
    }

    const applyFiltersAndFetch = () => {
      this.filters.investorName = (investorInput?.value || this.filters.investorName).trim() || this.filters.investorName;
      this.filters.year = Number(yearSelect?.value || this.filters.year);
      this.filters.quarter = Number(quarterSelect?.value || this.filters.quarter);
      this.filters.limit = Number(limitSelect?.value || this.filters.limit);
      (this.config as any).investorName = this.filters.investorName;
      (this.config as any).year = this.filters.year;
      (this.config as any).quarter = this.filters.quarter;
      (this.config as any).limit = this.filters.limit;
      this.setupChart();
    };

    yearSelect?.addEventListener('change', applyFiltersAndFetch);
    quarterSelect?.addEventListener('change', applyFiltersAndFetch);
    limitSelect?.addEventListener('change', applyFiltersAndFetch);
    investorInput?.addEventListener('change', applyFiltersAndFetch);
    investorInput?.addEventListener('keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        applyFiltersAndFetch();
      }
    });
  }

  private buildQueryParams(): Record<string, string | number> {
    const query: Record<string, string | number> = {
      investorName: this.filters.investorName,
      year: this.filters.year,
      quarter: this.filters.quarter,
      limit: this.filters.limit
    };
    if (this.filters.symbol) query.symbol = this.filters.symbol;
    return query;
  }

  private mapApiData(payload: any): InvestorAnalysisApiData | null {
    const dataBlock = payload?.data ?? payload;
    if (!dataBlock || typeof dataBlock !== 'object' || dataBlock.error) return null;

    const summary = dataBlock.summary ?? {};
    return {
      investor_name: dataBlock.investor_name ?? this.filters.investorName,
      year: Number(dataBlock.year ?? this.filters.year),
      quarter: Number(dataBlock.quarter ?? this.filters.quarter),
      symbol_filter: dataBlock.symbol_filter ?? this.filters.symbol ?? null,
      limit: Number(dataBlock.limit ?? this.filters.limit),
      data_date: dataBlock.data_date ?? null,
      summary: {
        total_holdings: Number(summary.total_holdings ?? 0),
        total_market_value: Number(summary.total_market_value ?? 0),
        total_market_value_billions: Number(summary.total_market_value_billions ?? 0),
        total_shares: Number(summary.total_shares ?? 0),
        max_weight: Number(summary.max_weight ?? 0),
        avg_weight: Number(summary.avg_weight ?? 0),
      },
      holdings_data: Array.isArray(dataBlock.holdings_data) ? dataBlock.holdings_data : [],
      matched_investors: Array.isArray(dataBlock.matched_investors) ? dataBlock.matched_investors : [],
    };
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async setupChart(symbol?: string): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');

    if (symbol) {
      this.filters.symbol = symbol;
      this.config.symbol = symbol;
    }

    try {
      const response = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/form-13f/investor-analysis`,
        query: this.buildQueryParams(),
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const json = await response.json();
      if (json && typeof json.isSuccess === 'boolean' && !json.isSuccess) {
        throw new Error(json.message || 'API returned isSuccess: false');
      }
      if (json?.data?.error) {
        throw new Error(String(json.data.error));
      }

      this.apiData = this.mapApiData(json);
      const fromApi = this.apiData?.data_date ?? json?.last_updated_at ?? null;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();
      this.updateDisplay();
      this.emit('dataUpdate', this.apiData);
    } catch (err) {
      console.error('Error fetching investor analysis data:', err);
      this.apiData = null;
      this.lastFetchedAt = new Date();
      this.updateDisplay();
    } finally {
      if (widget) widget.classList.remove('loading');
    }
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private formatNumber(value: number | string | null | undefined, digits = 0): string {
    const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
    if (isNaN(n)) return '-';
    return n.toLocaleString('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }

  private formatPercent(value: number | string | null | undefined, digits = 2): string {
    const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
    if (isNaN(n)) return '-';
    return `${n.toFixed(digits)}%`;
  }

  private formatMarketValue(item: InvestorHoldingItem): string {
    if (item.market_value_display) return item.market_value_display;
    if (item.market_value_millions != null) return `$${this.formatNumber(item.market_value_millions, 2)}M`;
    if (item.market_value != null) return `$${this.formatNumber(item.market_value)}`;
    return '-';
  }

  private updateDisplay(): void {
    this.updateLastUpdatedTooltip();
    this.renderTable();
  }

  private renderSummary(summary: InvestorAnalysisSummary, data: InvestorAnalysisApiData): string {
    const matched = data.matched_investors?.length
      ? `<span style="margin: 0 8px;">|</span><span>${this.formatNumber(data.matched_investors.length)} matched investors</span>`
      : '';
    const investorLabel = data.investor_name
      ? `<span>${this.escapeAttr(data.investor_name)}</span><span style="margin: 0 8px;">|</span>`
      : '';
    const periodLabel = `Q${data.quarter} ${data.year}`;

    return `
      <div class="insider-search-summary" style="margin-bottom: 12px; font-size: 13px; opacity: 0.85;" data-testing-id="investor-analysis-summary">
        ${investorLabel}
        <span>${periodLabel}</span>
        <span style="margin: 0 8px;">|</span>
        <span>${this.formatNumber(summary.total_holdings)} holdings</span>
        <span style="margin: 0 8px;">|</span>
        <span>$${this.formatNumber(summary.total_market_value_billions, 2)}B market value</span>
        <span style="margin: 0 8px;">|</span>
        <span>${this.formatNumber(summary.total_shares)} shares</span>
        <span style="margin: 0 8px;">|</span>
        <span>Max weight ${this.formatPercent(summary.max_weight)}</span>
        <span style="margin: 0 8px;">|</span>
        <span>Avg weight ${this.formatPercent(summary.avg_weight)}</span>
        ${matched}
      </div>
    `;
  }

  private renderTable(): void {
    const container = this.shadowRoot.querySelector('#investorAnalysisContainer');
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
    if (!container || !chartDv) return;

    const data = this.apiData;
    const holdings = data?.holdings_data ?? [];
    const hasData = holdings.length > 0;

    if (!data) {
      chartDv.removeAttribute('data-testing-id');
      container.innerHTML = getNoDataHTML();
      return;
    }

    // let html = this.renderSummary(data.summary, data);
    let html = '';

    if (!hasData) {
      chartDv.removeAttribute('data-testing-id');
      html += `<p>No data available</p>`;
      container.innerHTML = html;
      return;
    }

    chartDv.setAttribute('data-testing-id', 'investor-analysis-content');

    const rowsHtml = holdings.map((item, index) => {
      const symbol = item.symbol ?? '';
      const logoUrl = symbol
        ? `https://storage.googleapis.com/analytics-images-traderverse/stocks_v1/${symbol}.svg`
        : '';
      const name = item.security_name ?? '';
      const industry = item.industry_title ?? '';
      const marketValue = this.formatMarketValue(item);
      const weight = this.formatPercent(item.weight);
      const ownership = this.formatPercent(item.ownership_percentage ?? item.ownership);
      const shares = this.formatNumber(item.shares_number);
      const investorName = item.investor_name ?? data.investor_name ?? '';

      return `
      <div class="item body" data-testing-id="investor-analysis-row-${index}">
        <p>
          ${logoUrl ? `<img src="${logoUrl}" style="width:24px;height:24px;border-radius:4px;"/>` : ''}
          <span>${symbol}</span>
          ${name ? `<small style="opacity:.7;margin-left:4px;">${name}</small>` : ''}
        </p>
        <p>${investorName || '-'}</p>
        <p>${industry || '-'}</p>
        <p>${marketValue}</p>
        <p>${weight}</p>
        <p>${ownership}</p>
        <p>${shares}</p>
      </div>
    `;
    }).join('');

    html += `
    <div class="topIndexesMain eventsMain customGrid" style="--grid-template-columns: 1.6fr 1.2fr 1.4fr 1fr 0.8fr 0.8fr 1fr; min-width:720px;">
      <div class="item header">
        <p>Symbol</p>
        <p>Investor</p>
        <p>Industry</p>
        <p>Market Value</p>
        <p>Weight</p>
        <p>Ownership</p>
        <p>Shares</p>
      </div>
      ${rowsHtml}
    </div>
  `;

    container.innerHTML = html;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (id: any) => this.setupChart(id),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseInvestorAnalysis: typeof TraderverseInvestorAnalysis; } }
if (typeof window !== 'undefined') { window.TraderverseInvestorAnalysis = TraderverseInvestorAnalysis; }
